import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { Users, Mic, Dna, Check, Zap, X, Heart } from 'lucide-react';
import StaffAxisDemo from './StaffAxisDemo';
import NoteAxisDemo from './NoteAxisDemo';
import GrainOverlay from '../GrainOverlay';
import GenAxisDemo from './GenAxisDemo';
import VetAxisDemo from './VetAxisDemo';

// ===== Genome Sequence Background Helpers =====
const NUCLEOTIDES = ['A', 'T', 'G', 'C'];

const generateColumn = (length: number, highlightCount: number, seed: number) => {
  const letters: { char: string; highlighted: boolean }[] = [];
  let state = seed;
  const rand = () => {
    state = (state * 1664525 + 1013904223) & 0xffffffff;
    return (state >>> 0) / 0xffffffff;
  };
  const highlightIndices = new Set<number>();
  while (highlightIndices.size < highlightCount) {
    highlightIndices.add(Math.floor(rand() * length));
  }
  for (let i = 0; i < length; i++) {
    letters.push({
      char: NUCLEOTIDES[Math.floor(rand() * 4)],
      highlighted: highlightIndices.has(i),
    });
  }
  return letters;
};

interface ColumnConfig {
  xPercent: number;
  letterCount: number;
  highlightCount: number;
  duration: number;
  delay: number;
  seed: number;
}

const COLUMN_CONFIGS: ColumnConfig[] = [
  { xPercent: 4, letterCount: 60, highlightCount: 12, duration: 40, delay: 0, seed: 42 },
  { xPercent: 12, letterCount: 55, highlightCount: 10, duration: 45, delay: 2, seed: 137 },
  { xPercent: 20, letterCount: 65, highlightCount: 14, duration: 38, delay: 4, seed: 256 },
  { xPercent: 80, letterCount: 58, highlightCount: 11, duration: 42, delay: 1, seed: 519 },
  { xPercent: 88, letterCount: 52, highlightCount: 10, duration: 48, delay: 3, seed: 777 },
  { xPercent: 96, letterCount: 50, highlightCount: 9, duration: 44, delay: 1.5, seed: 333 },
];

// ===== Products Component =====

interface Product {
  id: string;
  icon: React.ComponentType<any>;
  title: string;
  tagline: string;
  description: string;
  features: string[];
  buttonText: string;
  badge: string | null;
  href: string;
  delay: number;
  accentColor: string;
}

interface ProductsProps {}

const Products: React.FC<ProductsProps> = () => {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generate genome column data once
  const genomeColumns = useMemo(
    () =>
      COLUMN_CONFIGS.map((config) => ({
        ...config,
        letters: generateColumn(config.letterCount, config.highlightCount, config.seed),
      })),
    []
  );

  const products: Product[] = [
    {
      id: 'staffaxis',
      icon: Users,
      title: 'StaffAxis',
      tagline: 'Human Resources & Team Management',
      description: 'Streamline your healthcare team management with intelligent scheduling, leave tracking, and compliance support tailored for medical practices.',
      features: [
        'Staff records & role management',
        'Smart scheduling & leave tracking',
        'Compliance & audit support',
        'Payroll integration ready'
      ],
      buttonText: 'Request a Demo',
      badge: null,
      href: '/contact',
      delay: 0,
      accentColor: 'bg-primary-500',
    },
    {
      id: 'noteaxis',
      icon: Mic,
      title: 'NoteAxis',
      tagline: 'AI-Powered Clinical Documentation',
      description: 'Transform voice notes into structured medical records with AI-powered transcription and intelligent diagnostic insights for healthcare professionals.',
      features: [
        'Voice-to-text transcription',
        'AI diagnostic suggestions',
        'Patient history integration',
        'Secure compliant storage'
      ],
      buttonText: 'Request a Demo',
      badge: 'Most Popular',
      href: '/contact',
      delay: 0.2,
      accentColor: 'bg-secondary-500',
    },
    {
      id: 'vetaxis',
      icon: Heart,
      title: 'VetAxis',
      tagline: 'Veterinary Practice Management',
      description: 'Lightweight, modern, and mobile-friendly veterinary practice management system for efficient client, animal, and billing workflows.',
      features: [
        'Client & animal management',
        'Clinical records & SOAP notes',
        'Appointment scheduling',
        'Invoicing & billing'
      ],
      buttonText: 'Request a Demo',
      badge: null,
      href: '/contact',
      delay: 0.4,
      accentColor: 'bg-accent-500',
    },
    {
      id: 'genaxis',
      icon: Dna,
      title: 'GenAxis',
      tagline: 'Genetics & Bioscience Analytics',
      description: 'Advanced genetic analysis and machine learning platform for research teams, labs, and bioscience innovations in healthcare.',
      features: [
        'Lab data integration',
        'Genetic analysis tools',
        'ML-powered predictions',
        'Research collaboration'
      ],
      buttonText: 'Get Notified',
      badge: 'Coming Soon',
      href: '/contact',
      delay: 0.6,
      accentColor: 'bg-primary-700',
    }
  ];

  const handleProductClick = (productHref: string) => {
    if (selectedProduct) return;
    window.location.href = productHref;
  };

  const handleDemoClick = (productId: string) => {
    if (selectedProduct === productId) {
      setIsAnimating(true);
      setTimeout(() => {
        setSelectedProduct(null);
      }, 100);
      setTimeout(() => {
        setIsAnimating(false);
      }, 800);
    } else {
      setIsAnimating(true);
      setSelectedProduct(productId);
      setTimeout(() => {
        setIsAnimating(false);
      }, 800);
    }
  };

  const getDemoComponent = (productId: string) => {
    const isActive = selectedProduct === productId && !isAnimating;
    switch (productId) {
      case 'staffaxis':
        return <StaffAxisDemo isActive={isActive} />;
      case 'noteaxis':
        return <NoteAxisDemo isActive={isActive} />;
      case 'genaxis':
        return <GenAxisDemo isActive={isActive} />;
      case 'vetaxis':
        return <VetAxisDemo isActive={isActive} />;
      default:
        return null;
    }
  };

  const getSelectedProduct = () => {
    return products.find(p => p.id === selectedProduct);
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
      transition: {
        staggerChildren: 0.1,
        staggerDirection: -1
      }
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section id="products" className="py-20 relative overflow-hidden">
      {/* ===== Genome Sequence Background ===== */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* Vertical guide lines */}
        {COLUMN_CONFIGS.map((config, i) => (
          <div
            key={`guide-${i}`}
            className="absolute top-0 bottom-0 w-px bg-primary-400/15 dark:bg-primary-500/8"
            style={{ left: `${config.xPercent}%` }}
          />
        ))}

        {/* Scrolling nucleotide columns */}
        {genomeColumns.map((col, colIndex) => {
          const doubledLetters = [...col.letters, ...col.letters];
          const singleBlockHeight = col.letterCount * 28;

          return (
            <motion.div
              key={`col-${colIndex}`}
              className="absolute top-0 flex flex-col items-center select-none"
              style={{
                left: `${col.xPercent}%`,
                transform: 'translateX(-50%)',
              }}
              animate={{ y: [0, -singleBlockHeight] }}
              transition={{
                duration: col.duration,
                repeat: Infinity,
                ease: 'linear',
                delay: col.delay,
              }}
            >
              {doubledLetters.map((letter, letterIndex) => (
                <span
                  key={`${colIndex}-${letterIndex}`}
                  className={`
                    font-mono text-sm leading-7 transition-colors duration-300
                    ${
                      letter.highlighted
                        ? 'text-primary-600/60 dark:text-primary-400/40 font-bold'
                        : 'text-primary-500/35 dark:text-primary-500/20'
                    }
                  `}
                >
                  {letter.char}
                </span>
              ))}
            </motion.div>
          );
        })}

        {/* Edge vignette to blend columns into background */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-white dark:from-transparent dark:via-transparent dark:to-transparent" style={{ zIndex: 1 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-white/60 dark:from-transparent dark:via-transparent dark:to-transparent" style={{ zIndex: 1 }} />
      </div>

      {/* GrainOverlay tint sits on top of genome background + vignettes — fade in at top to blend with Hero grain */}
      <GrainOverlay noGrain fade="top" />

      {/* ===== Products Content ===== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative" style={{ zIndex: 2 }}>
        <LayoutGroup>
          <AnimatePresence mode="wait">
            {!selectedProduct ? (
              // Products Card Stack View
              <motion.div
                key="products-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-16"
              >
                {/* Section Header */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark-900 dark:text-dark-100 mb-4">
                    Our Healthcare Solutions
                  </h2>
                  <p className="text-lg md:text-xl text-dark-500 dark:text-dark-300 max-w-3xl mx-auto leading-relaxed">
                    Independent, modular applications designed for modern healthcare workflows
                  </p>
                </motion.div>

                {/* Stacked Product Cards */}
                <motion.div
                  className="max-w-2xl mx-auto space-y-0"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      layoutId={`product-${product.id}`}
                      className={`product-card group relative flex flex-col ${index > 0 ? '-mt-3' : ''} ${
                        product.badge === 'Most Popular' ? 'ring-2 ring-primary-500/30 scale-[1.02]' : ''
                      } ${product.badge === 'Coming Soon' ? 'opacity-85' : ''}`}
                      variants={cardVariants}
                      whileHover={{
                        y: -6,
                        rotate: Math.random() > 0.5 ? 0.5 : -0.5,
                        transition: { duration: 0.3 }
                      }}
                      style={{ zIndex: products.length - index }}
                    >
                      {/* Left accent bar */}
                      <div className={`absolute left-0 top-6 bottom-6 w-1 rounded-full ${product.accentColor}`} />

                      <div className="flex flex-col sm:flex-row gap-6 pl-5">
                        {/* Icon in circle */}
                        <div className="flex-shrink-0">
                          <motion.div
                            layoutId={`icon-${product.id}`}
                            className={`w-14 h-14 ${product.accentColor} rounded-full flex items-center justify-center`}
                          >
                            <product.icon className="w-7 h-7 text-white" />
                          </motion.div>
                        </div>

                        {/* Content */}
                        <motion.div layoutId={`content-${product.id}`} className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-display text-xl font-bold text-dark-900 dark:text-dark-100">
                                {product.title}
                              </h3>
                              <p className="text-primary-600 dark:text-primary-400 font-semibold text-sm">
                                {product.tagline}
                              </p>
                            </div>

                            {/* Badge */}
                            {product.badge && (
                              <motion.div
                                layoutId={`badge-${product.id}`}
                                className={`flex-shrink-0 ml-2 px-3 py-1 rounded-full text-xs font-bold ${
                                  product.badge === 'Most Popular'
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-secondary-500 text-white'
                                }`}
                              >
                                {product.badge}
                              </motion.div>
                            )}
                          </div>

                          <p className="text-dark-500 dark:text-dark-300 text-sm leading-relaxed mb-4">
                            {product.description}
                          </p>

                          {/* Features as pills */}
                          <div className="flex flex-wrap gap-2 mb-5">
                            {product.features.map((feature, fIndex) => (
                              <span
                                key={fIndex}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                              >
                                {product.badge === 'Coming Soon' ? (
                                  <Zap className="w-3 h-3" />
                                ) : (
                                  <Check className="w-3 h-3" />
                                )}
                                {feature}
                              </span>
                            ))}
                          </div>

                          {/* Buttons */}
                          <div className="flex flex-col sm:flex-row gap-3">
                            <motion.button
                              onClick={() => handleProductClick(product.href)}
                              className="btn-primary text-sm px-6 py-2.5"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              {product.buttonText}
                            </motion.button>

                            <motion.button
                              onClick={() => handleDemoClick(product.id)}
                              className="btn-secondary text-sm px-6 py-2.5"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              Learn More
                            </motion.button>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            ) : (
              // Demo View - Hero Layout
              <motion.div
                key="demo-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="min-h-screen flex flex-col"
              >
                {/* Close Button */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => handleDemoClick(selectedProduct)}
                  className="absolute top-24 right-8 z-50 w-12 h-12 bg-secondary-50 dark:bg-dark-800 rounded-full shadow-warm flex items-center justify-center hover:bg-secondary-100 dark:hover:bg-dark-700 transition-colors"
                >
                  <X className="w-6 h-6 text-dark-600 dark:text-dark-400" />
                </motion.button>

                {/* Hero Demo Layout */}
                <div className="flex-1 flex items-center justify-center pb-20">
                  <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      {/* Product Info - Left Side */}
                      <motion.div
                        layoutId={`product-${selectedProduct}`}
                        className="relative"
                      >
                        {getSelectedProduct()?.badge && (
                          <motion.div
                            layoutId={`badge-${selectedProduct}`}
                            className={`absolute -top-4 -right-4 px-4 py-2 rounded-full text-sm font-bold ${
                              getSelectedProduct()?.badge === 'Most Popular'
                                ? 'bg-primary-500 text-white'
                                : 'bg-secondary-500 text-white'
                            }`}
                          >
                            {getSelectedProduct()?.badge}
                          </motion.div>
                        )}

                        {/* Product Icon */}
                        <motion.div
                          layoutId={`icon-${selectedProduct}`}
                          className={`w-20 h-20 ${getSelectedProduct()?.accentColor} rounded-full flex items-center justify-center mb-8`}
                        >
                          {getSelectedProduct() && React.createElement(getSelectedProduct()!.icon, {
                            className: 'w-10 h-10 text-white'
                          })}
                        </motion.div>

                        {/* Product Content */}
                        <motion.div layoutId={`content-${selectedProduct}`}>
                          <h1 className="font-display text-4xl md:text-5xl font-bold text-dark-900 dark:text-dark-100 mb-4">
                            {getSelectedProduct()?.title}
                          </h1>
                          <p className="text-xl text-primary-600 dark:text-primary-400 font-semibold mb-6">
                            {getSelectedProduct()?.tagline}
                          </p>
                          <p className="text-lg text-dark-500 dark:text-dark-300 leading-relaxed mb-8">
                            {getSelectedProduct()?.description}
                          </p>

                          {/* Features */}
                          <ul className="space-y-4 mb-8">
                            {getSelectedProduct()?.features.map((feature, index) => (
                              <motion.li
                                key={index}
                                className="flex items-center gap-3 text-dark-600 dark:text-dark-300"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * index }}
                              >
                                <Check className="w-5 h-5 text-primary-500 flex-shrink-0" />
                                <span className="text-lg">{feature}</span>
                              </motion.li>
                            ))}
                          </ul>

                          {/* CTA Button */}
                          <motion.button
                            onClick={() => handleProductClick(getSelectedProduct()?.href || '')}
                            className="btn-primary text-lg px-8 py-3"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {getSelectedProduct()?.buttonText}
                          </motion.button>
                        </motion.div>
                      </motion.div>

                      {/* Demo Component - Right Side */}
                      <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex justify-center items-center"
                      >
                        {getDemoComponent(selectedProduct)}
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </LayoutGroup>
      </div>
    </section>
  );
};

export default Products;
